import * as React from 'react';
import { useEffect } from 'react';
import {
  SubmitErrorHandler,
  SubmitHandler
} from 'react-hook-form/dist/types/form';
import {
  PmPaymentFormProps,
  XBezahldienstFormProps,
  EPayBLFormProps,
  PaymentSubFormProps
} from './Payment.types';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../shared/notification/useNotification';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GovosApiRoutes,
  govosClient,
  invalidateConfiguredOnlinediensteCache,
  invalidateServiceTemplatesCache
} from '../../api/govos-api';
import {
  EditPaymentService,
  PmPaymentParams,
  XBezahldienstParams,
  EPayBLParams
} from '../../api/govos.types';

export function usePaymentForm({
  onSuccess,
  primaryButtonClickCount,
  setPrimaryButtonClickCount,
  isParentFormValid,
  startAppId,
  processId,
  templateId,
  parentState,
  handleSubmit,
  shouldSaveAsTemplate,
  newTemplateName
}: PaymentSubFormProps & {
  handleSubmit: (
    onValid: SubmitHandler<
      Partial<PmPaymentFormProps & XBezahldienstFormProps & EPayBLFormProps>
    >,
    onInvalid?: SubmitErrorHandler<
      Partial<PmPaymentFormProps & XBezahldienstFormProps & EPayBLFormProps>
    >
  ) => (e?: React.BaseSyntheticEvent) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();

  const isTemplateMode = templateId != null;

  const { mutate: createTemplate } = useMutation({
    mutationFn: async (configuration: string) =>
      notifyUserFromRequest(
        () =>
          govosClient.post(GovosApiRoutes.serviceTemplates(startAppId), {
            name: newTemplateName,
            serviceType: 'payment',
            configuration
          }),
        {
          successMessage: t('api.saveAsTemplate.successMessage'),
          errorMessage: t('api.saveAsTemplate.errorMessage')
        }
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: invalidateServiceTemplatesCache()
      });
    }
  });

  const { mutate: updatePaymentConfig } = useMutation({
    mutationFn: async (payload: EditPaymentService) =>
      notifyUserFromRequest(
        () =>
          isTemplateMode
            ? govosClient.put(
                GovosApiRoutes.serviceTemplate(startAppId, templateId),
                payload
              )
            : govosClient.post(
                GovosApiRoutes.paymentService(startAppId, processId!),
                payload
              ),
        {
          successMessage: t('api.paymentService.successMessage'),
          errorMessage: t('api.paymentService.errorMessage'),
          loadingMessage: t('api.paymentService.loadingMessage')
        }
      ),
    onSuccess: (_, payload) => {
      if (isTemplateMode) {
        void queryClient.invalidateQueries({
          predicate: invalidateServiceTemplatesCache()
        });
      } else {
        void queryClient.invalidateQueries({
          predicate: invalidateConfiguredOnlinediensteCache(processId ?? '')
        });
        if (shouldSaveAsTemplate && newTemplateName?.trim()) {
          createTemplate(JSON.stringify(payload));
        }
      }
      onSuccess?.();
    }
  });

  const onSubmit = handleSubmit(async (data) => {
    if (!isParentFormValid) {
      setPrimaryButtonClickCount(0);
      return;
    }

    if (data.serviceId !== undefined) {
      const { serviceId, ...params } = data;
      const payload: EditPaymentService = {
        serviceId: serviceId,
        serviceCode: parentState.selectedService.serviceCode,
        policy: data.policy ?? 'INACTIVE',
        params:
          Object.keys(params).length > 0
            ? (params as
                | PmPaymentParams
                | XBezahldienstParams
                | EPayBLParams
                | undefined)
            : undefined
      };
      updatePaymentConfig(payload);
    }
  });

  useEffect(() => {
    if (primaryButtonClickCount > 0) {
      onSubmit();
    }
  }, [primaryButtonClickCount, isParentFormValid, onSubmit]);
}
